import React, { useContext } from 'react'
import { css } from '@emotion/core'
import { Bleed, Bleedable, Ellipsis, colors } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { Link } from '@fs/zion-router'
import { format } from '@fs/zion-locale/date-fns'
import CardAddRemoveButton from '../CardAddRemoveButton'
import { PlaylistContext } from '../PlaylistContext'
import useIsMobile from '../../hooks/useIsMobile'
import getRelativeUrl from '../../helpers/UrlHelper'
import { removeTags } from '../RichTextDisplayer'

import placeholderImg from '../../images/card-placeholder.jpg'

const cardCss = css`
  height: 100%;
  background: var(--background-secondary);
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  width: 250px;
  box-shadow: 0px 24px 38px rgba(0, 0, 0, 0.14), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 11px 15px rgba(0, 0, 0, 0.2);
  transition: 0.1s;
  position: relative;
  padding-bottom: 52px;
  &:hover {
    filter: brightness(1.07);
  }
`

const imageWrapperCss = css`
  position: relative;
`

const imgCss = css`
  width: 100%;
  height: 180px;
  object-fit: cover;
  background: white;
`

const timeStampCss = css`
  position: absolute;
  bottom: 16px;
  color: #fc4e6d;
`

const tagCss = css`
  border-radius: 2px;
  display: inline-block;
  padding: 2px 7px;
  text-transform: uppercase;
  font-size: 12px;
`

const seriesTagCss = css`
  background: #3fcde3;
  color: var(--background-primary);
  font-weight: 600;
  ${tagCss}
`
const languageTagCss = css`
  background: var(--background-secondary);
  color: #fff;
  font-weight: 600;
  ${tagCss}
  position: absolute;
  bottom: 16px;
  left: 16px;
`

const contentCss = css`
  padding-top: 10px;
`

const primaryTextCss = css`
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  padding: 6px 0 4px;
  word-break: break-word;
`

const secondaryTextCss = css`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  color: #a0a0a0;
  word-break: break-word;
`

const liveTagCss = css`
  color: #270d1b;
  background: #fc4e6d;
  font-weight: 600;
  ${tagCss}
  position: absolute;
  bottom: 40px;
  left: 16px;
`

const durationTagCss = css`
  background: var(--background-secondary);
  ${tagCss}
  position: absolute;
  bottom: 16px;
  right: 16px;
`

const mobileCardCss = css`
  background-color: transparent;
  border: none;
  color: ${colors.billboard.gray00};
  display: grid;
  grid-column-gap: 12px;
  grid-template-columns: auto 1fr;
  justify-items: start;
  align-items: center;
  padding: 0;
  text-align: left;
  max-width: calc(100vw - 48px);
  word-break: break-word;
`
const mobileImgCss = css`
  border-radius: 4px;
  height: 70px;
  object-fit: cover;
  object-position: center;
  width: 70px;
`
const mobileTitleCss = css`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
`
const mobileSubtitleWrapperCss = css`
  align-items: center;
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 8px;
  justify-content: start;
`
const mobileSubtitleCss = css`
  color: ${colors.billboard.gray15};
  font-size: 12px;
`
const mobileCardInfoCss = css`
  /* This bumps it up a tiny bit to center better */
  margin-bottom: 8px;
`

const mobileLangTagCss = css`
  position: relative;
  bottom: 0;
  left: 0;
`

const newTabProps = {
  target: '_blank',
  rel: 'noopener noreferrer',
}

export default function Card({
  extraContent,
  goLiveTime,
  image = placeholderImg,
  primaryText,
  secondaryText,
  isSeries,
  isLive,
  spokenLanguage,
  sessionLength,
  url,
  contentUUID,
  isPlaylistable,
  isInPlaylist,
  isSearch = false,
  imageFit = 'cover',
  largeMobileImg = false,
  openInNewTab = false,
  'data-testid': dataTestId,
  actionButton,
}) {
  const { addPlaylistItem, removePlaylistItem } = useContext(PlaylistContext)

  const [t] = useTranslation()
  const isMobile = useIsMobile()
  const handleKey = (e) => {
    const { keyCode } = e
    if (keyCode === 'enter' || keyCode === 'space') {
      onclick(e)
    }
  }

  const title = removeTags(primaryText)
  const description = removeTags(secondaryText)

  // content editors are entering partial information for some sessions. Since all items have a required
  // title to be considered minimally complete, we can assume that no title means the card is
  // not ready to show the user.
  if (!title) {
    return null
  }

  if (isMobile && !isSearch) {
    const largeImgOverrides = {
      backgroundColor: '#fff',
      height: 110,
      objectFit: 'contain',
      width: 110,
    }
    return (
      <Link noLinkStyles css={mobileCardCss} to={getRelativeUrl(url)} {...(openInNewTab ? newTabProps : {})}>
        <img src={image} alt="" aria-hidden css={mobileImgCss} style={largeMobileImg ? largeImgOverrides : undefined} />
        <div css={mobileCardInfoCss}>
          <div css={mobileTitleCss}>{title}</div>
          <div css={mobileSubtitleWrapperCss}>
            {spokenLanguage && <div css={[languageTagCss, mobileLangTagCss]}>{spokenLanguage}</div>}
            {isSeries && <div css={seriesTagCss}>{t('card.tag.series', 'Series')}</div>}
            <div css={mobileSubtitleCss}>
              <Ellipsis lineClamp={4}>{description}</Ellipsis>
            </div>
          </div>
        </div>
      </Link>
    )
  }

  return (
    // Not sure why, but having a tab-index is causing the page to scroll down immediately to the last card when in theateroverlay when we have a tabIndex set on these
    <Link noLinkStyles to={getRelativeUrl(url)} {...(openInNewTab ? newTabProps : {})}>
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus */}
      <div css={cardCss} onKeyUp={handleKey} data-testid="card" role="button">
        <Bleedable top={16} sides={16} bottom={16}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Bleed all>
              <div css={imageWrapperCss}>
                <img loading="lazy" src={image} css={imgCss} style={{ objectFit: imageFit }} alt="" aria-hidden />
                {spokenLanguage && <div css={languageTagCss}>{spokenLanguage}</div>}
                {isLive && <div css={liveTagCss}>{t('card.tag.live', 'Live')}</div>}
                {sessionLength && <div css={durationTagCss}>{sessionLength}</div>}
              </div>
            </Bleed>
            <div css={contentCss}>
              {isSeries && <div css={seriesTagCss}>{t('card.tag.series', 'Series')}</div>}
              <div css={primaryTextCss} title={title}>
                <Ellipsis lineClamp={2}>{title}</Ellipsis>
              </div>
              <div css={secondaryTextCss}>
                <Ellipsis lineClamp={3}>{description}</Ellipsis>
              </div>
              {actionButton}
              {isPlaylistable && contentUUID && (
                <CardAddRemoveButton
                  absolutePositioned
                  data-testid={`addremove-${dataTestId}`}
                  isInPlaylist={isInPlaylist}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (isInPlaylist) {
                      return removePlaylistItem({ contentUUID })
                    }
                    return addPlaylistItem({ contentUUID, type: isSeries ? 'series' : 'session' })
                  }}
                />
              )}
              {extraContent}
              {isLive && goLiveTime && <div css={timeStampCss}>{format(new Date(goLiveTime), 'Pp')}</div>}
            </div>
          </div>
        </Bleedable>
      </div>
    </Link>
  )
}
